/**
 * @class ThumbgenRenderer
 */
import {waitFor, error, warn} from "../../utils/utils";
import {thumbGenUrl} from "../../utils/globals";

export default class ThumbgenRenderer {

    env = "prod";
    userId = null;
    project = {};
    designJson = null;
    layout = null;

    angle = 0;
    textEnabled = true;
    skipPhotosCount = true;
    addImageDropIcon = true;
    masks = {};
    elmId = null;
    pId = null;
    orientation = "portrait";

    /**
     * Constructing a new instance of ThumbgenRenderer
     * @memberOf ThumbgenRenderer
     * @param {String} env
     * @param {String} userId
     * @param {Object} project
     * @param {Object} designJson
     * @param {Object} layout
     * @param {Object} thumbParams
     * @example
     */
    constructor(env, userId, project, designJson, layout, thumbParams = {}) {
        /*Defaults*/
        this.env = env;
        this.userId = userId;
        this.project = project;
        this.designJson = designJson;
        this.layout = layout;

        Object.assign(this, thumbParams);

        this.validate();
    }

    /**
     * Validates the initialized input
     * @private
     * @memberOf SugarWrapper
     */
    validate() {
        if (this.project === null || typeof this.project !== "object" || Object.keys(this.project).length === 0) {
            error("Validation Error", "input project is invalid");
        }

        if (this.designJson === null || typeof this.designJson !== "object" || Object.keys(this.designJson).length === 0) {
            error("Validation Error", "input designJson is invalid");
        }

        if (this.layout === null || typeof this.layout !== "object" || Object.keys(this.layout).length === 0) {
            error("Validation Error", "input layout is invalid");
        }
    }

    /**
     * Renders the product using Thumbgen
     * @public
     * @memberOf ThumbgenRenderer
     * @param {Boolean} returnImage - if to return as an image (default is false, as a canvas)
     * @return HTMLCanvasElement|String
     * @example
     * renderer.render().then(canvas=>{
     *     console.log(canvas)
     * });
     */
    render(returnImage = false) {
        return new Promise((resolve, reject) => {
            try {

                this.constructor.checkAndLoadThumbGen(this.env).then(() => {
                    let project = {product: this.project};

                    if (this.productType === "book" || this.productType === "prints") {
                        project = this.project;
                    }

                    window._MgThumbGenerator.init(this.productType, this.productId, project, this.layout, this.designJson, () => {
                    });

                    try {

                        const params = {textEnabled: this.textEnabled, orientation: this.orientation};
                        if (typeof this.masks === "object" && Object.keys(this.masks).length !== 0) params.masks = this.masks;

                        window._MgThumbGenerator.generate(parseInt(this.angle), this.elmId || this.productId, false, canvas => {
                            resolve(canvas);
                        }, true, params, this.userId, this.skipPhotosCount, this.addImageDropIcon);

                    } catch (e) {
                        error("Error", `failed to render item with ThumbGen - ${e.message}`);
                    }

                }).catch(e => {
                    warn(e);
                    error("Error", `failed to load MgThumbGen - ${e.message}`);
                });

            } catch (e) {
                warn(e);
                error("Error", "failed to render with ThumbGen");
            }
        });
    }

    get productType() {
        if (this.project.book) {
            return "book";
        } else if (this.project.prints) {
            return "prints";
        } else if (this.project.info?.product === "MATISSE_CALENDAR") {
            return "calendar";
        } else if (this.project.info?.productType === "none") {
            return "mug";
        } else {
            return this.project.info?.productType;
        }
    }

    get productId() {
        switch (this.productType) {
            case 'book':
                return `${this.project.book.info.style}_${this.project.book.info.size}`;
            case 'calendar':
                return `${this.project.info.style}_${this.project.info.size}`;
            case 'prints':
                return `print_${this.project.prints.info.size}`;
            default:
                return `${this.project.info.sizeId}/${this.project.info.occasionId}/${this.project.info.styleId}/-S${this.pId ? this.pId : this.project.info.sku}S-`;
        }
    }

    static checkAndLoadThumbGen(env) {
        return new Promise((resolve, reject) => {

            const initiateThumbGen = () => {
                if (!window._MgThumbGenerator) window._MgThumbGenerator = new MgThumbGenerator(env);
                resolve();
            };

            // Already loaded
            if (window.MgThumbGenerator) {
                initiateThumbGen();
                return;
            }


            // Load thumbgen if not loaded
            if ((typeof window.MgThumbGenerator !== "function")) {
                if (window.loadingThumbGen) {
                    waitFor(() => window._MgThumbGenerator, 5000, "failed to load thumbgen").then(initiateThumbGen);
                } else {
                    const script = document.createElement("script");
                    script.type = "text/javascript";

                    script.onload = () => {
                        if (window.MgThumbGenerator) {
                            initiateThumbGen();
                            window.loadingThumbGen = false;
                        } else {
                            reject();
                        }
                    };

                    script.onerror = (e) => {
                        window.loadingThumbGen = false;
                        reject(e);
                    };

                    // Prevent multiple loading
                    window.loadingThumbGen = true;
                    script.src = thumbGenUrl(env);

                    document.getElementsByTagName('head')[0].appendChild(script);
                }
            } else {
                initiateThumbGen();
            }
        })
    }
}
